import { CalendarIcon } from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { UseFormReturn, FieldValues, Path } from "react-hook-form";

interface FormDatePickerProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  id: Path<T>;
  label: string | React.ReactNode;
  classes?: string;
  placeholder?: string;
  disabled?: boolean;
}

export default function FormDatePicker<T extends FieldValues>({
  form,
  id,
  label,
  classes,
  placeholder,
  disabled = false,
}: FormDatePickerProps<T>) {
  return (
    <FormField
      control={form.control}
      name={id}
      render={({ field }) => {
        let formattedField;
        try {
          const formatted = format(field.value, "PPP");
          formattedField = formatted;
        } catch {
          formattedField = format(new Date(), "PPP");
        }
        return (
          <FormItem className="">
            <FormLabel className="">{label}</FormLabel>
            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant="outline"
                    disabled={disabled}
                    className={cn(
                      "sm:min-w-[240px] w-full text-left",
                      !field.value && "text-muted-foreground",
                      "mb-2",
                      classes,
                    )}
                  >
                    {field.value !== "Invalid Date" ? (
                      <span>{String(formattedField)}</span>
                    ) : (
                      <span>{placeholder || "Pick a date"}</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-60" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  defaultMonth={new Date()}
                  mode="single"
                  selected={field.value}
                  onSelect={field.onChange}
                />
              </PopoverContent>
            </Popover>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
